import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { apiErrorMessage } from "@/AuthContext";
import { filterCallsByPeriod } from "@/lib/period";
import { usePeriod } from "@/PeriodContext";

const CallsContext = createContext(null);

export function CallsProvider({ children }) {
  const { period } = usePeriod();
  const [calls, setCalls] = useState([]);
  const [license, setLicense] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const refreshCalls = useCallback(async () => {
    const callsRes = await api.get("/calls");
    setCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
  }, []);

  const refreshLicense = useCallback(async () => {
    const licenseRes = await api.get("/license/status");
    setLicense(licenseRes.data);
    if (licenseRes.data?.has_access === true || licenseRes.data?.active === true) {
      try {
        await refreshCalls();
      } catch (err) {
        setError(apiErrorMessage(err));
      }
    } else {
      setCalls([]);
    }
    return licenseRes.data;
  }, [refreshCalls]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const licenseRes = await api.get("/license/status");
        if (cancelled) return;
        setLicense(licenseRes.data);
        if (licenseRes.data?.has_access === true || licenseRes.data?.active === true) {
          const callsRes = await api.get("/calls");
          if (cancelled) return;
          setCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
        } else {
          setCalls([]);
        }
      } catch (err) {
        if (!cancelled) setError(apiErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => {
    const visible = filterCallsByPeriod(calls, period);
    return { calls, visible, license, error, loading, period, refreshCalls, refreshLicense };
  }, [calls, license, error, loading, period, refreshCalls, refreshLicense]);

  return <CallsContext.Provider value={value}>{children}</CallsContext.Provider>;
}

export function useCalls() {
  const ctx = useContext(CallsContext);
  if (!ctx) throw new Error("useCalls must be used inside CallsProvider");
  return ctx;
}
